import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/Card';
import { LineChart } from '../../components/charts';
import { RefreshCw, ShoppingCart, Package, BarChart2 } from 'lucide-react';

const formatCurrency = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function formatShortNumber(value) {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = abs < 10_000_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}M`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = abs < 10_000 ? v.toFixed(1) : Math.round(v).toString();
    return `${s}K`;
  }
  return n.toString();
}

function computeIsoWeekParts(ymd) {
  const d = new Date(ymd + 'T00:00:00');
  const isoDow = ((d.getDay() + 6) % 7) + 1;
  const thursday = new Date(d);
  thursday.setDate(d.getDate() + (4 - isoDow));
  const isoYear = thursday.getFullYear();
  const jan1 = new Date(isoYear, 0, 1);
  const jan1IsoDow = ((jan1.getDay() + 6) % 7) + 1;
  const firstThursday = new Date(jan1);
  firstThursday.setDate(jan1.getDate() + (4 - jan1IsoDow));
  const diffDays = Math.floor((thursday - firstThursday) / 86400000);
  const isoWeek = Math.floor(diffDays / 7) + 1;
  return { isoYear, isoWeek, isoDow };
}

export default function WooOverview() {
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('revenue');
  const [siteName, setSiteName] = useState('Store');
  const [monthlyAllAgg, setMonthlyAllAgg] = useState({}); // { 'YYYY-MM': revenue }
  // Empty placeholders (endpoints will be wired later)
  const [dailyRows, setDailyRows] = useState([]);
  const [channelSeries] = useState({ labels: [], datasets: [] });
  const [monthlyByChannelAll] = useState([]);
  const [channelMetric, setChannelMetric] = useState('revenue');
  const [weekdayChannel, setWeekdayChannel] = useState('All');
  const [weeklyMetric, setWeeklyMetric] = useState('revenue');
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [weekdayByWeek, setWeekdayByWeek] = useState([]);
  // All-time variants (independent of date filter)
  const [weekdayByWeekAll, setWeekdayByWeekAll] = useState([]);
  const [weeklySummaryAll, setWeeklySummaryAll] = useState([]);

  const lineData = useMemo(() => {
    return (dailyRows || []).map(r => {
      const label = String(r.day || r.date || r.order_date || '').slice(0, 10);
      const value =
        metric === 'revenue'
          ? Number(r.revenue || 0)
          : metric === 'orders'
          ? Number(r.orders_count || 0)
          : Number(r.items_count || 0);
      return { label, value };
    });
  }, [dailyRows, metric]);

  const totalRevenue = (dailyRows || []).reduce((a, r) => a + Number(r.revenue || 0), 0);
  const totalOrders = (dailyRows || []).reduce((a, r) => a + Number(r.orders_count || 0), 0);
  const totalItems = (dailyRows || []).reduce((a, r) => a + Number(r.items_count || 0), 0);
  const aov = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  const availableChannels = useMemo(() => {
    return ['All'];
  }, []);
  useEffect(() => {
    if (!weekdayChannel && availableChannels.length > 0) {
      setWeekdayChannel(availableChannels[0]);
    }
  }, [availableChannels, weekdayChannel]);

  const toYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchDaily = async (r) => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      const now = new Date();
      let s, e;
      if (r === 'yesterday') {
        const y = new Date(now); y.setDate(y.getDate() - 1);
        s = toYmd(y); e = toYmd(y);
      } else if (r === '7d') {
        const d = new Date(now); d.setDate(d.getDate() - 7);
        s = toYmd(d); e = toYmd(now);
      } else if (r === '30d') {
        const d = new Date(now); d.setDate(d.getDate() - 30);
        s = toYmd(d); e = toYmd(now);
      } else if (r === 'thisMonth') {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        s = toYmd(d); e = toYmd(now);
      } else if (r === 'lastMonth') {
        const d1 = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const d2 = new Date(now.getFullYear(), now.getMonth(), 0);
        s = toYmd(d1); e = toYmd(d2);
      } else if (r === 'all') {
        // all-time: backend handles when all=true
      } else {
        const d = new Date(now); d.setDate(d.getDate() - 30);
        s = toYmd(d); e = toYmd(now);
      }
      const params = r === 'all'
        ? { all: true }
        : { startDate: s, endDate: e };
      const resp = await axios.get('https://api.glowytics.com/api/woocommerce/analytics/orders-daily', {
        params,
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId }
      });
      const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
      setDailyRows(rows);
    } catch (e) {
      setDailyRows([]);
    } finally {
      setLoading(false);
    }
  };

  const applyQuickRange = (id) => {
    setRange(id);
    fetchDaily(id);
  };

  useEffect(() => {
    fetchDaily(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all-time daily rows once, and derive:
  // - monthlyAllAgg (for Monthly Revenue table)
  // - weekdayByWeekAll (for Weekday by ISO Week table)
  // - weeklySummaryAll (for WoW Summary table)
  useEffect(() => {
    (async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const headers = { 'X-Brand-ID': brandId };
        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
        const res = await fetch(`${base}/api/woocommerce/analytics/orders-daily?all=true`, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        const rows = Array.isArray(js?.rows) ? js.rows : [];
        // Monthly aggregation
        const agg = {};
        for (const r of rows) {
          const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
          if (!ymd) continue;
          const m = ymd.slice(0, 7);
          agg[m] = (agg[m] || 0) + Number(r.revenue || 0);
        }
        setMonthlyAllAgg(agg);
        // Weekday by ISO Week (all-time)
        const weekdayOut = [];
        for (const r of rows) {
          const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
          if (!ymd) continue;
          const parts = computeIsoWeekParts(ymd);
          weekdayOut.push({
            iso_year: parts.isoYear,
            iso_week: parts.isoWeek,
            iso_dow: parts.isoDow,
            day: ymd,
            orders_count: Number(r.orders_count || 0),
            items_count: Number(r.items_count || 0),
            revenue: Number(r.revenue || 0),
          });
        }
        setWeekdayByWeekAll(weekdayOut);
        // Week-over-Week Summary (all-time)
        const byWeek = new Map();
        for (const r of rows) {
          const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
          if (!ymd) continue;
          const parts = computeIsoWeekParts(ymd);
          const key = `${parts.isoYear}-${String(parts.isoWeek).padStart(2, '0')}`;
          if (!byWeek.has(key)) {
            byWeek.set(key, { iso_year: parts.isoYear, iso_week: parts.isoWeek, revenue: 0, orders_count: 0, items_count: 0 });
          }
          const row = byWeek.get(key);
          row.revenue += Number(r.revenue || 0);
          row.orders_count += Number(r.orders_count || 0);
          row.items_count += Number(r.items_count || 0);
        }
        const weeks = Array.from(byWeek.values()).sort((a, b) => a.iso_year - b.iso_year || a.iso_week - b.iso_week);
        for (let i = 0; i < weeks.length; i++) {
          const prev = i > 0 ? weeks[i - 1] : null;
          const cur = weeks[i];
          const pct = (curVal, prevVal) => (prevVal && prevVal !== 0) ? ((curVal - prevVal) / prevVal) * 100 : null;
          cur.revenue_wow_pct = pct(cur.revenue, prev?.revenue || 0);
          cur.orders_wow_pct = pct(cur.orders_count, prev?.orders_count || 0);
          cur.items_wow_pct = pct(cur.items_count, prev?.items_count || 0);
        }
        setWeeklySummaryAll(weeks.reverse());
      } catch {
        setMonthlyAllAgg({});
        setWeekdayByWeekAll([]);
        setWeeklySummaryAll([]);
      }
    })();
  }, []);

  // Build weekday-by-week matrix from daily rows
  useEffect(() => {
    try {
      const src = dailyRows || [];
      const out = [];
      for (const r of src) {
        const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
        if (!ymd) continue;
        const parts = computeIsoWeekParts(ymd);
        out.push({
          iso_year: parts.isoYear,
          iso_week: parts.isoWeek,
          iso_dow: parts.isoDow,
          day: ymd,
          orders_count: Number(r.orders_count || 0),
          items_count: Number(r.items_count || 0),
          revenue: Number(r.revenue || 0),
        });
      }
      setWeekdayByWeek(out);
    } catch {
      setWeekdayByWeek([]);
    }
  }, [dailyRows]);

  // Build week-over-week summary
  useEffect(() => {
    try {
      const src = dailyRows || [];
      if (src.length === 0) { setWeeklySummary([]); return; }
      const agg = new Map();
      for (const r of src) {
        const ymd = String(r.day || r.date || r.order_date || '').slice(0, 10);
        if (!ymd) continue;
        const parts = computeIsoWeekParts(ymd);
        const key = `${parts.isoYear}-${String(parts.isoWeek).padStart(2, '0')}`;
        if (!agg.has(key)) {
          agg.set(key, { iso_year: parts.isoYear, iso_week: parts.isoWeek, revenue: 0, orders_count: 0, items_count: 0 });
        }
        const row = agg.get(key);
        row.revenue += Number(r.revenue || 0);
        row.orders_count += Number(r.orders_count || 0);
        row.items_count += Number(r.items_count || 0);
      }
      const weeks = Array.from(agg.values()).sort((a, b) => a.iso_year - b.iso_year || a.iso_week - b.iso_week);
      for (let i = 0; i < weeks.length; i++) {
        const prev = i > 0 ? weeks[i - 1] : null;
        const cur = weeks[i];
        const pct = (curVal, prevVal) => (prevVal && prevVal !== 0) ? ((curVal - prevVal) / prevVal) * 100 : null;
        cur.revenue_wow_pct = pct(cur.revenue, prev?.revenue || 0);
        cur.orders_wow_pct = pct(cur.orders_count, prev?.orders_count || 0);
        cur.items_wow_pct = pct(cur.items_count, prev?.items_count || 0);
      }
      setWeeklySummary(weeks.reverse());
    } catch {
      setWeeklySummary([]);
    }
  }, [dailyRows]);

  // Load Woo site (store) name for row label
  useEffect(() => {
    (async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const headers = { 'X-Brand-ID': brandId };
        const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
        const res = await fetch(`${base}/api/auth/connections`, { credentials: 'include', headers });
        const js = await res.json().catch(() => ({}));
        let name = 'Store';
        if (Array.isArray(js)) {
          const w = js.find(c => String(c.platform || '').toUpperCase() === 'WOOCOMMERCE');
          name = (w?.accountName || w?.customerId || name);
        } else if (js && typeof js === 'object') {
          const w = js.WOOCOMMERCE || js.woocommerce;
          name = (w?.accountName || w?.customerId || name);
        }
        if (name) setSiteName(String(name));
      } catch {
        // keep default
      }
    })();
  }, []);
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-3">
            <img src="/woocommercelogo.webp" alt="WooCommerce" className="h-8 object-contain" />
            <h1 className="text-xl font-bold text-gray-900">Overview</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {[
                { id: 'yesterday', label: 'Yesterday' },
                { id: '7d', label: '7 Days' },
                { id: '30d', label: '30 Days' },
                { id: 'thisMonth', label: 'This Month' },
                { id: 'lastMonth', label: 'Last Month' },
                { id: 'all', label: 'All Time' }
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    range === opt.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-900'
                  }`}
                  onClick={() => applyQuickRange(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              className="p-2 text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-60"
              onClick={() => {}}
              disabled={loading}
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-blue-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-lg font-semibold mt-1">₺{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-50">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-indigo-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Orders</p>
                <p className="text-lg font-semibold mt-1">{totalOrders.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-indigo-50">
                <BarChart2 className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-amber-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Total Items</p>
                <p className="text-lg font-semibold mt-1">{totalItems.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-50">
                <Package className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
          <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100 hover:bg-emerald-50 transition-colors cursor-default">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-gray-500">Average Order Value</p>
                <p className="text-lg font-semibold mt-1">₺{formatCurrency(aov)}</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-50">
                <BarChart2 className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </Card>
        </div>
        <div className="flex items-center justify-between mt-5 mb-3">
          <div className="text-sm font-medium text-gray-800">Daily Trend</div>
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'revenue' ? 'bg-white shadow-sm text-emerald-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('revenue')}
            >
              Revenue
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'orders' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('orders')}
            >
              Orders
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs ${metric === 'items' ? 'bg-white shadow-sm text-amber-700' : 'text-gray-700 hover:text-gray-900'}`}
              onClick={() => setMetric('items')}
            >
              Items
            </button>
          </div>
        </div>
        <div className="h-72">
          {channelSeries.datasets.length > 0 ? (
            <LineChart data={{ labels: channelSeries.labels, datasets: channelSeries.datasets }} />
          ) : lineData.length > 0 ? (
            <LineChart
              data={{
                labels: lineData.map(d => d.label),
                datasets: [{
                  label: metric === 'revenue' ? 'Revenue' : metric === 'orders' ? 'Orders' : 'Items',
                  data: lineData.map(d => d.value),
                  borderColor: metric === 'revenue' ? 'rgb(16, 185, 129)' : metric === 'orders' ? 'rgb(37, 99, 235)' : 'rgb(217, 119, 6)',
                  backgroundColor: metric === 'revenue' ? 'rgba(16, 185, 129, 0.1)' : (metric === 'orders' ? 'rgba(37, 99, 235, 0.1)' : 'rgba(217, 119, 6, 0.1)'),
                  fill: true,
                  tension: 0.4
                }]
              }}
            />
          ) : (
            <div className="text-sm text-gray-500">No chart data.</div>
          )}
        </div>
      </div>

      {/* Monthly Revenue Pivot (columns: months, row: site) */}
      <div className="bg-white border rounded-2xl p-4 shadow-sm mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">Monthly Revenue</div>
        </div>
        {(() => {
          // Use all-time monthly aggregation (independent of date filter)
          const months = Object.keys(monthlyAllAgg || {}).sort();
          if (months.length === 0) {
            return <div className="text-sm text-gray-500">No monthly data.</div>;
          }
          const fmtMonth = (ym) => {
            try {
              const d = new Date(`${ym}-01T00:00:00`);
              return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
            } catch {
              return ym;
            }
          };
          return (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b align-bottom">
                    <th className="py-2 pr-4 w-48">Site</th>
                    {months.map((m) => (
                      <th key={m} className="py-2 pr-4">{fmtMonth(m)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{siteName}</td>
                    {months.map((m) => (
                      <td key={m} className="py-2 pr-4">₺{formatCurrency((monthlyAllAgg || {})[m] || 0)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Weekly/Daily tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekday by ISO Week */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">Weekday by ISO Week</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Metric</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weeklyMetric}
                onChange={(e) => setWeeklyMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Week</th>
                    <th className="py-2 pr-3">Mon</th>
                    <th className="py-2 pr-3">Tue</th>
                    <th className="py-2 pr-3">Wed</th>
                    <th className="py-2 pr-3">Thu</th>
                    <th className="py-2 pr-3">Fri</th>
                    <th className="py-2 pr-3">Sat</th>
                    <th className="py-2 pr-3">Sun</th>
                    <th className="py-2 pr-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const grouped = new Map();
                    for (const r of weekdayByWeekAll) {
                      const y = Number(r.iso_year);
                      const w = Number(r.iso_week);
                      const key = `${y}-W${String(w).padStart(2, '0')}`;
                      if (!grouped.has(key)) grouped.set(key, { year: y, week: w, byDow: Array(8).fill(0) });
                      const v = weeklyMetric === 'revenue' ? Number(r.revenue || 0)
                                : weeklyMetric === 'orders' ? Number(r.orders_count || 0)
                                : Number(r.items_count || 0);
                      grouped.get(key).byDow[Number(r.iso_dow)] = (grouped.get(key).byDow[Number(r.iso_dow)] || 0) + v;
                    }
                    const rows = Array.from(grouped.values()).sort((a, b) => b.year - a.year || b.week - a.week);
                    if (rows.length === 0) return (<tr><td className="py-3 text-gray-500" colSpan={9}>No data.</td></tr>);
                    const fmt = (x) => weeklyMetric === 'revenue' ? `₺${formatShortNumber(x)}` : formatShortNumber(x);
                    return rows.map((rw) => {
                      const total = rw.byDow.slice(1).reduce((s, x) => s + (x || 0), 0);
                      return (
                        <tr key={`${rw.year}-${rw.week}`} className="border-b">
                          <td className="py-2 pr-3 font-medium">{`W${String(rw.week).padStart(2, '0')} ${rw.year}`}</td>
                          {[1,2,3,4,5,6,7].map((d) => (
                            <td key={d} className="py-2 pr-3">{fmt(rw.byDow[d] || 0)}</td>
                          ))}
                          <td className="py-2 pr-3 font-semibold">{fmt(total)}</td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Week-over-Week Summary */}
        <div className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-lg font-semibold">Week-over-Week Summary</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Metric</span>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={weeklyMetric}
                onChange={(e) => setWeeklyMetric(e.target.value)}
              >
                <option value="revenue">Revenue</option>
                <option value="orders">Orders</option>
                <option value="items">Items</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[360px] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left border-b">
                    <th className="py-2 pr-3">Week</th>
                    <th className="py-2 pr-3">Revenue</th>
                    <th className="py-2 pr-3">Orders</th>
                    <th className="py-2 pr-3">Items</th>
                    <th className="py-2 pr-3">Δ Rev</th>
                    <th className="py-2 pr-3">Δ Orders</th>
                    <th className="py-2 pr-3">Δ Items</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklySummaryAll.length === 0 ? (
                    <tr><td className="py-3 text-gray-500" colSpan={7}>No data.</td></tr>
                  ) : weeklySummaryAll.map((r, idx) => {
                    const wLabel = `W${String(r.iso_week).padStart(2, '0')} ${r.iso_year}`;
                    const fmtC = (v) => `₺${formatShortNumber(v)}`;
                    const fmtN = (v) => formatShortNumber(v);
                    const fmtPct = (v) => (v === null || v === undefined) ? '-' : `${(Number(v)).toFixed(1)}%`;
                    const color = (v) => (v === null || v === undefined) ? 'text-gray-500' : (v >= 0 ? 'text-emerald-600' : 'text-rose-600');
                    const hi = (col) => weeklyMetric === col ? 'font-semibold text-gray-900' : 'text-gray-700';
                    return (
                      <tr key={idx} className="border-b">
                        <td className={`py-2 pr-3 font-medium`}>{wLabel}</td>
                        <td className={`${hi('revenue')} py-2 pr-3`}>{fmtC(r.revenue || 0)}</td>
                        <td className={`${hi('orders')} py-2 pr-3`}>{fmtN(r.orders_count || 0)}</td>
                        <td className={`${hi('items')} py-2 pr-3`}>{fmtN(r.items_count || 0)}</td>
                        <td className={`${color(r.revenue_wow_pct)} py-2 pr-3`}>{fmtPct(r.revenue_wow_pct)}</td>
                        <td className={`${color(r.orders_wow_pct)} py-2 pr-3`}>{fmtPct(r.orders_wow_pct)}</td>
                        <td className={`${color(r.items_wow_pct)} py-2 pr-3`}>{fmtPct(r.items_wow_pct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


